// =============================================================================
// Web i18n adapter — SC-008
//
// DOM-coupled host adapter for the platform-agnostic core in ./index. Supplies
// browser locale detection (localStorage → navigator → default) and
// persistence, then renders the shared <I18nProvider>. Imported ONLY by the
// Vite web apps (admin, vendor); the mobile bundle never pulls this file.
// =============================================================================
import { useEffect } from 'react';
import type { ReactNode, ReactElement } from 'react';
import { DEFAULT_LOCALE, I18nProvider, resolveLocale } from './index';
import type { Locale } from './index';

/** localStorage key for the persisted UI language. */
export const WEB_LOCALE_STORAGE_KEY = 'tradelogic.locale';

/** Read the persisted locale, else the browser's, else the residency default. */
export function readInitialWebLocale(): Locale {
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem(WEB_LOCALE_STORAGE_KEY);
    if (stored) return resolveLocale(stored);
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    return resolveLocale(navigator.language);
  }
  return DEFAULT_LOCALE;
}

/** Persist the chosen locale and reflect it on <html lang> for a11y/SEO. */
export function persistWebLocale(locale: Locale): void {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(WEB_LOCALE_STORAGE_KEY, locale);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

export interface WebI18nProviderProps {
  readonly children: ReactNode;
}

/** Drop-in i18n provider for the web shells; wires detection + persistence. */
export function WebI18nProvider({ children }: WebI18nProviderProps): ReactElement {
  const initialLocale = readInitialWebLocale();
  useEffect(() => {
    // Reflect the initial locale on <html lang> once on mount.
    if (typeof document !== 'undefined') document.documentElement.lang = initialLocale;
  }, [initialLocale]);
  return (
    <I18nProvider initialLocale={initialLocale} onLocaleChange={persistWebLocale}>
      {children}
    </I18nProvider>
  );
}
